import type { Transition, Variants } from "motion/react";
import { dirOf, getLang } from "./i18n/lang";

/**
 * One motion vocabulary for the whole app, mirroring docs/design/tokens.css
 * (`--duration-micro` 150ms, `--duration-ui` 220ms, `--ease` cubic-bezier).
 *
 * Motion values have to be numbers in seconds, so they cannot literally read
 * the CSS variables — but nothing else is allowed to invent its own timing.
 */
export const EASE = [0.2, 0, 0, 1] as const;
/** The settle curve: arrives fast, stops without a bounce. Matches --ease-out. */
export const EASE_OUT = [0.16, 1, 0.3, 1] as const;
export const DURATION_MICRO = 0.15;
export const DURATION_UI = 0.22;
export const DURATION_SLOW = 0.42;

export const micro: Transition = { duration: DURATION_MICRO, ease: EASE };
export const ui: Transition = { duration: DURATION_UI, ease: EASE };
export const slow: Transition = { duration: DURATION_SLOW, ease: EASE_OUT };

/** Sheets and shared elements: springs read as physical, curves read as UI. */
export const sheetSpring: Transition = {
  type: "spring",
  stiffness: 460,
  damping: 42,
  mass: 0.9,
};

/**
 * The one shared element in the app: a list thumbnail growing into the record
 * it opens. Slower and heavier than a press, because the thing being moved is
 * a picture rather than a control — it should read as the work being carried
 * to the wall, not as a panel snapping open.
 *
 * The two ends crop differently on purpose (a row thumb fills its square, a
 * hero never crops), so the picture is genuinely reframed in flight. What
 * covers that is the hero's own blurred thumbnail underneath — `.heroBlur` is
 * already filling the frame while this runs, so the reframing lands as focus
 * rather than as a stretch.
 */
export const heroSpring: Transition = {
  type: "spring",
  stiffness: 320,
  damping: 36,
  mass: 0.8,
};

/**
 * The one spring a control uses to answer a finger. Stiff enough that the
 * rebound lands inside the press itself rather than trailing it.
 */
export const pressSpring: Transition = {
  type: "spring",
  stiffness: 620,
  damping: 34,
  mass: 0.5,
};

/**
 * Three press depths, and only three: the further a control is from the
 * finger's idea of a button, the less it should move. A 48px CTA can take a
 * 3% squeeze; a full-width list row moving that much reads as a glitch.
 */
export const pressButton = {
  whileTap: { scale: 0.97 },
  transition: pressSpring,
} as const;

export const pressTile = {
  whileTap: { scale: 0.975 },
  transition: pressSpring,
} as const;

export const pressRow = {
  whileTap: { scale: 0.99 },
  transition: pressSpring,
} as const;

/** Kept as the old name so nothing outside has to know the depths are named. */
export const pressable = pressButton;

/** Enter eases out, exit eases in and shorter — DESIGN.md's rule. */
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: ui },
  exit: { opacity: 0, y: -4, transition: micro },
};

export const fade: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: ui },
  exit: { opacity: 0, transition: micro },
};

/**
 * Rows arrive in sequence rather than all at once. Capped so a long page does
 * not spend a second and a half revealing itself.
 */
export const listStagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.035, delayChildren: 0.02 } },
};

export const listItem: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: DURATION_SLOW, ease: EASE_OUT } },
  /* Leaves the way everything else leaves: shorter, and without the travel. */
  exit: { opacity: 0, transition: micro },
};

/**
 * The slower cousin of `listStagger`, for a page made of sections rather than
 * a list made of rows: fewer, larger blocks, so each one gets more of the beat.
 */
export const sectionStagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.055, delayChildren: 0.04 } },
};

/**
 * "Forward" always arrives from the leading edge — the left in RTL, the right
 * in LTR — so the gesture reads the same whichever language is switched on.
 *
 * The scale is what makes it read as depth rather than as a slide: the arriving
 * screen comes up from just under the surface, the leaving one drops back.
 */
export function slideVariants(direction: 1 | -1): Variants {
  const rtl = dirOf(getLang()) === "rtl";
  const distance = 28 * direction * (rtl ? 1 : -1);
  return {
    hidden: { opacity: 0, x: -distance, scale: 0.99 },
    show: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { duration: DURATION_SLOW, ease: EASE_OUT },
    },
    exit: { opacity: 0, x: distance, scale: 0.995, transition: micro },
  };
}
